import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../../api/client';

const STATUS_COLOR = {
  Open: 'bg-blue-50 text-blue-700 border-blue-200',
  Logged: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Troubleshooting: 'bg-amber-50 text-amber-700 border-amber-200',
  Resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Closed: 'bg-gray-100 text-gray-500 border-gray-200',
};

const STATUS_DOT = {
  Open: 'bg-blue-500',
  Logged: 'bg-indigo-500',
  Troubleshooting: 'bg-amber-500',
  Resolved: 'bg-emerald-500',
  Closed: 'bg-gray-400',
};

const TIMELINE_DOT = {
  Open: 'bg-blue-500 ring-blue-100',
  Logged: 'bg-indigo-500 ring-indigo-100',
  Troubleshooting: 'bg-amber-500 ring-amber-100',
  Resolved: 'bg-emerald-500 ring-emerald-100',
  Closed: 'bg-gray-400 ring-gray-100',
};

export default function BHELTicketDetail() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    client.get(`/bhel/tickets/${id}/`).then(r => setTicket(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-[#C1278D] border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Loading ticket…</p>
      </div>
    </div>
  );
  if (!ticket) return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <p className="text-red-500 font-semibold">Ticket not found.</p>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/bhel/tickets')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3D3562] hover:text-[#C1278D] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          Tickets
        </button>
        <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
        <span className="font-mono text-xs font-bold text-[#3D3562] bg-purple-50 px-2 py-1 rounded-lg border border-purple-100">{ticket.ticket_number}</span>
      </div>

      {/* Main Ticket Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Magenta top border accent */}
        <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #C1278D, #3D3562)' }} />

        <div className="p-6 space-y-5">
          {/* Title + Status */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">{ticket.subject}</h1>
              {ticket.description && (
                <p className="text-gray-500 text-sm mt-2 leading-relaxed">{ticket.description}</p>
              )}
            </div>
            <span className={`inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-full border flex-shrink-0 ${STATUS_COLOR[ticket.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
              <span className={`w-2 h-2 rounded-full ${STATUS_DOT[ticket.status] || 'bg-gray-400'}`} />
              {ticket.status}
            </span>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-50">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-[#3D3562]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Location</p>
                <p className="text-gray-800 font-semibold text-sm">{ticket.location_name || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-[#3D3562]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Room</p>
                <p className="text-gray-800 font-semibold text-sm">{ticket.room_name || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-[#3D3562]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Assigned To</p>
                <p className="text-gray-800 font-semibold text-sm">{ticket.assigned_to_name || '—'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-[#3D3562]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Category</p>
                <p className="text-gray-800 font-semibold text-sm">{ticket.category}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-[#3D3562]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Fault Reported</p>
                <p className="text-gray-800 font-semibold text-sm">{new Date(ticket.fault_reported_at).toLocaleString('en-IN')}</p>
              </div>
            </div>

            {ticket.resolved_at && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-0.5">Resolved At</p>
                  <p className="text-emerald-700 font-semibold text-sm">{new Date(ticket.resolved_at).toLocaleString('en-IN')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Timeline */}
      {ticket.history?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#3D3562] to-[#272244] flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h2 className="font-bold text-gray-800">Status Timeline</h2>
            <span className="ml-auto text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{ticket.history.length} events</span>
          </div>
          <div className="p-6">
            <div className="relative">
              {/* Connecting line */}
              <div className="absolute left-3.5 top-4 bottom-4 w-px bg-gray-100" />
              <div className="space-y-5">
                {ticket.history.map((h, i) => {
                  const dotClass = TIMELINE_DOT[h.to] || 'bg-gray-400 ring-gray-100';
                  return (
                    <div key={i} className="relative flex items-start gap-4 pl-1">
                      <div className={`relative z-10 w-6 h-6 rounded-full ring-4 flex items-center justify-center flex-shrink-0 mt-0.5 ${dotClass}`}>
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                      <div className="flex-1 min-w-0 bg-gray-50 rounded-xl px-4 py-3 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLOR[h.from] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                            {h.from}
                          </span>
                          <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_COLOR[h.to] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                            {h.to}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5">
                          <span className="font-semibold text-gray-500">{h.by}</span>
                          <span className="mx-1.5">·</span>
                          {new Date(h.at).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
